import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link, Navigate } from 'react-router-dom';
import Header from '../components/Header';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { governingDocsData, governingDocsVersion, governingDocsUpdated, governingDocsIdRedirects } from '../data/governingDocsData';
import './GoverningDocs.css';

const formatTag = (tag) => tag.label ? `${tag.label}: ${tag.value}` : tag.value;

// The tag labels that get a filter dropdown, in display order. `label` matches
// the tag.label in the data; `display` is the (pluralized) dropdown heading.
const FILTER_CATEGORIES = [
  { label: 'Type', display: 'Type' },
  { label: 'Department', display: 'Department' },
  { label: 'Division', display: 'Division' },
  { label: 'Public Body', display: 'Public Body' },
];

function GoverningDocsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [activeTags, setActiveTags] = useState(new Set(searchParams.getAll('tag')));
  const [copiedId, setCopiedId] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  const isAllSelected = activeTags.size === 0;

  // Per-category values (alphabetical) with document counts, plus a value→label map.
  const categories = useMemo(() => {
    return FILTER_CATEGORIES.map(cat => {
      const counts = new Map();
      governingDocsData.forEach(doc => doc.tags.forEach(t => {
        if (t.label === cat.label) counts.set(t.value, (counts.get(t.value) || 0) + 1);
      }));
      const values = [...counts.entries()]
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => a.value.localeCompare(b.value));
      return { ...cat, values };
    });
  }, []);

  const labelOfValue = useMemo(() => {
    const m = new Map();
    governingDocsData.forEach(doc => doc.tags.forEach(t => {
      if (!m.has(t.value)) m.set(t.value, t.label || '');
    }));
    return m;
  }, []);

  const toggleTag = (tagValue) => {
    setActiveTags(prev => {
      const next = new Set(prev);
      if (next.has(tagValue)) {
        next.delete(tagValue);
      } else {
        next.add(tagValue);
      }
      return next;
    });
  };

  // Select "All" for one category: clear every active value that belongs to it.
  const selectCategoryAll = (label) => {
    setActiveTags(prev => {
      const next = new Set(prev);
      [...next].forEach(v => { if (labelOfValue.get(v) === label) next.delete(v); });
      return next;
    });
  };

  const clearTags = () => setActiveTags(new Set());

  // Close an open dropdown on Escape, or on any click that is not on a dropdown
  // trigger or inside an open panel — so the "All" button and the blank space
  // around the filter buttons both dismiss it.
  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDown = (e) => {
      if (e.target.closest('.gov-docs-dropdown-trigger') || e.target.closest('.gov-docs-dropdown-panel')) return;
      setOpenDropdown(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpenDropdown(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openDropdown]);

  useEffect(() => {
    document.title = 'Brookline Governing Documents';
  }, []);

  // Reflect the active filters and search in the URL query string so they survive
  // navigating into a document's detail page and back (and can be linked/bookmarked).
  useEffect(() => {
    const params = new URLSearchParams();
    [...activeTags].forEach(t => params.append('tag', t));
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    setSearchParams(params, { replace: true });
  }, [activeTags, searchQuery, setSearchParams]);

  const excludedFilterLabels = new Set(["Article", "Authorizing Body", "Town Meeting"]);

  const allTags = useMemo(() => {
    const tagMap = new Map();
    governingDocsData.forEach(doc => doc.tags.forEach(t => {
      if (!tagMap.has(t.value) && !excludedFilterLabels.has(t.label)) {
        tagMap.set(t.value, t);
      }
    }));
    return [...tagMap.values()].sort((a, b) => formatTag(a).localeCompare(formatTag(b)));
  }, []);

  const filteredDocs = useMemo(() => {
    let docs = governingDocsData;

    if (activeTags.size > 0) {
      // Group active tags by label: same label = union (OR), different labels = intersection (AND)
      const tagsByLabel = new Map();
      activeTags.forEach(tagValue => {
        const tag = allTags.find(t => t.value === tagValue);
        const label = tag ? tag.label : '';
        if (!tagsByLabel.has(label)) {
          tagsByLabel.set(label, []);
        }
        tagsByLabel.get(label).push(tagValue);
      });

      docs = docs.filter(doc =>
        [...tagsByLabel.values()].every(groupValues =>
          groupValues.some(v => doc.tags.some(t => t.value === v))
        )
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      docs = docs.filter(doc =>
        doc.name.toLowerCase().includes(q) ||
        (doc.authorizingBody && doc.authorizingBody.toLowerCase().includes(q)) ||
        (doc.dateAdopted && doc.dateAdopted.toLowerCase().includes(q)) ||
        (doc.notes && doc.notes.toLowerCase().includes(q)) ||
        doc.tags.some(t => formatTag(t).toLowerCase().includes(q))
      );
    }

    return docs;
  }, [searchQuery, activeTags]);

  const copyToClipboard = (text, docId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(docId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // Build display text grouped by label: "or" within groups, "+" between groups
  const activeTagGroups = useMemo(() => {
    if (activeTags.size === 0) return [];
    const groups = new Map();
    activeTags.forEach(tv => {
      const tag = allTags.find(t => t.value === tv) || { label: '', value: tv };
      const label = tag.label || '';
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(formatTag(tag));
    });
    return [...groups.values()];
  }, [activeTags, allTags]);

  return (
    <div className="gov-docs-page">
      <a href="#main-content" className="gov-docs-skip-link">Skip to main content</a>
      <Header title="Brookline Governing Documents" />

      <main id="main-content" tabIndex={-1} className="gov-docs-content">
        <p className="gov-docs-version">{governingDocsVersion} - Updated {governingDocsUpdated}</p>

        <div className="gov-docs-controls">
          <div className="gov-docs-search-wrapper">
            <input
              type="text"
              className="gov-docs-search"
              placeholder="Search documents..."
              aria-label="Search documents"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="gov-docs-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <div className="gov-docs-filter-bar">
            <button
              className={`gov-docs-all-btn ${isAllSelected ? 'active' : ''}`}
              onClick={() => { clearTags(); setOpenDropdown(null); }}
              aria-pressed={isAllSelected}
            >
              All ({governingDocsData.length})
            </button>

            {categories.map(cat => {
              const selectedCount = cat.values.filter(v => activeTags.has(v.value)).length;
              const isCatAll = selectedCount === 0;
              const open = openDropdown === cat.label;
              return (
                <div className="gov-docs-dropdown" key={cat.label}>
                  <button
                    className={`gov-docs-dropdown-trigger ${selectedCount > 0 ? 'has-selection' : ''} ${open ? 'open' : ''}`}
                    onClick={() => setOpenDropdown(open ? null : cat.label)}
                    aria-expanded={open}
                    aria-haspopup="true"
                  >
                    {cat.display}{selectedCount > 0 ? ` (${selectedCount})` : ''}
                    <span className="gov-docs-caret" aria-hidden="true"></span>
                  </button>
                  {open && (
                    <div className="gov-docs-dropdown-panel" role="group" aria-label={`Filter by ${cat.display}`}>
                      <label className="gov-docs-check">
                        <input
                          type="checkbox"
                          checked={isCatAll}
                          onChange={() => selectCategoryAll(cat.label)}
                        />
                        <span className="gov-docs-check-text">All ({governingDocsData.length})</span>
                      </label>
                      {cat.values.map(v => (
                        <label className="gov-docs-check" key={v.value}>
                          <input
                            type="checkbox"
                            checked={activeTags.has(v.value)}
                            onChange={() => toggleTag(v.value)}
                          />
                          <span className="gov-docs-check-text">{v.value} ({v.count})</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="gov-docs-count" aria-live="polite" aria-atomic="true">
          {filteredDocs.length} document{filteredDocs.length !== 1 ? 's' : ''}
          {activeTagGroups.length > 0 && (
            <span className="gov-docs-filter-label"> tagged {activeTagGroups.map((group, gi) => (
              <span key={gi}>
                {gi > 0 && ' + '}
                {group.map((name, ni) => <span key={ni}>{ni > 0 && ' or '}"{name}"</span>)}
              </span>
            ))} <button className="gov-docs-clear-filter" onClick={clearTags} aria-label="Clear filters">×</button></span>
          )}
          {searchQuery && <span className="gov-docs-filter-label"> matching "{searchQuery}"</span>}
        </div>

        <div className="gov-docs-list">
          {filteredDocs.map(doc => {
            const articleTag = doc.tags.find(t => t.label === 'Article');
            return (
            <div key={doc.id} className="gov-docs-item">
              <Link to={`/governing-documents/${doc.id}`} state={{ from: location.search }} className="gov-docs-item-name">
                {doc.name}
              </Link>
              <div className="gov-docs-item-actions">
                {doc.websiteUrl && (
                  <>
                    <a href={doc.websiteUrl} target="_blank" rel="noopener noreferrer"
                       className="gov-docs-icon-link" title="Open website" aria-label="Open website (opens in new tab)">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="2" y1="12" x2="22" y2="12"/>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                    </a>
                    <button
                      className={`gov-docs-copy-btn ${copiedId === doc.id + '-web' ? 'copied' : ''}`}
                      onClick={() => copyToClipboard(doc.websiteUrl, doc.id + '-web')}
                      title="Copy website link" aria-label="Copy website link"
                    >
                      {copiedId === doc.id + '-web' ? '✓' :
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      }
                    </button>
                  </>
                )}
                {doc.documentUrl && (
                  <>
                    <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer"
                       className="gov-docs-icon-link doc-link" title="Open document" aria-label="Open document (PDF, opens in new tab)">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10 9 9 9 8 9"/>
                      </svg>
                    </a>
                    <button
                      className={`gov-docs-copy-btn ${copiedId === doc.id + '-doc' ? 'copied' : ''}`}
                      onClick={() => copyToClipboard(doc.documentUrl, doc.id + '-doc')}
                      title="Copy document link" aria-label="Copy document link"
                    >
                      {copiedId === doc.id + '-doc' ? '✓' :
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      }
                    </button>
                  </>
                )}
                {articleTag && articleTag.url && (
                  <>
                    <a href={articleTag.url} target="_blank" rel="noopener noreferrer"
                       className="gov-docs-icon-link article-link" title="Open article" aria-label="Open article (opens in new tab)">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                      </svg>
                    </a>
                    <button
                      className={`gov-docs-copy-btn ${copiedId === doc.id + '-article' ? 'copied' : ''}`}
                      onClick={() => copyToClipboard(articleTag.url, doc.id + '-article')}
                      title="Copy article link" aria-label="Copy article link"
                    >
                      {copiedId === doc.id + '-article' ? '✓' :
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      }
                    </button>
                  </>
                )}
              </div>
              {articleTag ? (
                <div className="gov-docs-item-date">Article {articleTag.value}</div>
              ) : doc.dateAdopted ? (
                <div className="gov-docs-item-date">{doc.dateAdopted}</div>
              ) : null}
            </div>
            );
          })}
          {filteredDocs.length === 0 && (
            <div className="gov-docs-empty">No documents match your search.</div>
          )}
        </div>

        <DisclaimerFooter />
      </main>
    </div>
  );
}

function GoverningDocDetail() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [copiedField, setCopiedField] = useState(null);

  // Return to the list with whatever filters/search were active when the user
  // arrived here (passed via Link state); falls back to the unfiltered list for
  // direct/deep links that carry no state.
  const backTo = `/governing-documents${location.state?.from || ''}`;

  const doc = governingDocsData.find(d => d.id === docId);

  useEffect(() => {
    if (doc) {
      document.title = `${doc.name} - Brookline Governing Documents`;
    }
  }, [doc]);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  if (!doc) {
    // A retired id: send the old URL to the document's current one, keeping
    // any query/hash and the list-filter state used by the back button.
    const redirectId = governingDocsIdRedirects[docId];
    if (redirectId) {
      return (
        <Navigate
          to={`/governing-documents/${redirectId}${location.search}${location.hash}`}
          state={location.state}
          replace
        />
      );
    }
    return (
      <div className="gov-docs-page">
        <Header title="Brookline Governing Documents" />
        <main className="gov-docs-content">
          <div className="gov-docs-not-found">
            <h2>Document not found</h2>
            <Link to={backTo}>Back to all documents</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="gov-docs-page">
      <a href="#main-content" className="gov-docs-skip-link">Skip to main content</a>
      <Header title="Brookline Governing Documents" />
      <main id="main-content" tabIndex={-1} className="gov-docs-content">
        <button className="gov-docs-back" onClick={() => navigate(backTo)}>
          ← Back to all documents
        </button>

        <div className="gov-docs-detail">
          <h1 className="gov-docs-detail-title">{doc.name}</h1>

          <div className="gov-docs-detail-meta">
            {doc.authorizingBody && (
              <div className="gov-docs-detail-field">
                <span className="gov-docs-detail-label">Authorizing Body</span>
                <span className="gov-docs-detail-value">{doc.authorizingBody}</span>
              </div>
            )}
            {doc.dateAdopted && (
              <div className="gov-docs-detail-field">
                <span className="gov-docs-detail-label">Date {doc.dateAction || 'Adopted'}</span>
                <span className="gov-docs-detail-value">{doc.dateAdopted}</span>
              </div>
            )}
            <div className="gov-docs-detail-field">
              <span className="gov-docs-detail-label">Tags</span>
              <div className="gov-docs-detail-tags">
                {doc.tags.map(tag => (
                  <Link key={tag.value} to={`/governing-documents?tag=${encodeURIComponent(tag.value)}`}
                        className="gov-docs-detail-tag">
                    {formatTag(tag)}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="gov-docs-detail-links">
            {doc.websiteUrl && (
              <div className="gov-docs-detail-link-row">
                <a href={doc.websiteUrl} target="_blank" rel="noopener noreferrer"
                   className="gov-docs-detail-link website-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                  Visit Website<span className="visually-hidden"> (opens in new tab)</span>
                </a>
                <button
                  className={`gov-docs-detail-copy ${copiedField === 'web' ? 'copied' : ''}`}
                  onClick={() => copyToClipboard(doc.websiteUrl, 'web')}
                  aria-label={copiedField === 'web' ? 'Website link copied' : 'Copy website link'}
                >
                  {copiedField === 'web' ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            )}
            {doc.documentUrl && (
              <div className="gov-docs-detail-link-row">
                <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer"
                   className="gov-docs-detail-link document-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                  View Document<span className="visually-hidden"> (PDF, opens in new tab)</span>
                </a>
                <button
                  className={`gov-docs-detail-copy ${copiedField === 'doc' ? 'copied' : ''}`}
                  onClick={() => copyToClipboard(doc.documentUrl, 'doc')}
                  aria-label={copiedField === 'doc' ? 'Document link copied' : 'Copy document link'}
                >
                  {copiedField === 'doc' ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            )}
            {!doc.websiteUrl && !doc.documentUrl && (
              <div className="gov-docs-detail-no-links">No links available for this document.</div>
            )}
          </div>

          {doc.notes && (
            <div className="gov-docs-detail-notes">
              <span className="gov-docs-detail-label">Notes</span>
              <p>{doc.notes}</p>
            </div>
          )}
        </div>

        <DisclaimerFooter />
      </main>
    </div>
  );
}

function GoverningDocs() {
  return null; // Routing handled by App.js
}

export { GoverningDocsList, GoverningDocDetail };
export default GoverningDocs;
